import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  CircularProgress,
  Grid,
} from '@mui/material';
import dayjs from 'dayjs';
import {useFormik} from 'formik';
import update from 'immutability-helper';
import _, {isEmpty} from 'lodash';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';

import API from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {
  NetworkDiagnosticsRoutingItem,
  NetworkDiagnosticsRoutingResponse,
  RoutingQueryParams,
} from '../../../interfaces/CommunicationNode';
import {ExportField} from '../../../interfaces/Export';
import reduxSelectors from '../../../redux/selectors';
import DataGrid, {DataGridColumn, DataGridRef} from '../../common/DataGrid';
import {DateRangeSelect} from '../../selectors/DateRangeSelect';
import {SectionMultipleSelect} from '../../selectors/SectionSelect';
import NetworkDiagnosticsRoutingExportButton from './NetworkDiagnosticsRoutingExportButton';

interface Props {
  value?: NetworkDiagnosticsRoutingData;
  onUpdate?: (value?: NetworkDiagnosticsRoutingData) => void;
}

const DEFAULT_SHOWN_FIELDS = [
  'external_id',
  'name',
  'routing_parent_1_address',
  'routing_parent_1_rssi',
  'routing_parent_1_percentage',
  'routing_parent_2_address',
  'routing_parent_2_rssi',
  'routing_parent_2_percentage',
  'routing_parent_3_address',
  'routing_parent_3_rssi',
  'routing_parent_3_percentage',
  'routing_parent_4_address',
  'routing_parent_4_rssi',
  'routing_parent_4_percentage',
  'routing_parent_5_address',
  'routing_parent_5_rssi',
  'routing_parent_5_percentage',
  'routing_min_hops',
  'routing_max_hops',
  'routing_mean_hops',
  'routing_lost_parent_count',
  'routing_changed_parent_count',
];

export interface NetworkDiagnosticsRoutingData {
  params: RoutingQueryParams;
  selectedIds?: number[];
  exportFields: ExportField[];
  columnWidths: Record<string, number>;
  columnOrder?: string[];
  shownFields?: string[];
}

export const getNetworkDiagnosticsRoutingData =
  (): NetworkDiagnosticsRoutingData => {
    return {
      params: {
        date_start: dayjs().format('YYYY-MM-DD'),
        date_end: dayjs().format('YYYY-MM-DD'),
        limit: 10,
        page: 0,
        order: 'external_id',
        dir: 'ASC',
        section_ids: undefined,
      },
      selectedIds: undefined,
      exportFields: [],
      columnWidths: {},
      columnOrder: undefined,
      shownFields: DEFAULT_SHOWN_FIELDS,
    };
  };

export const NetworkDiagnosticsRouting = ({value, onUpdate}: Props) => {
  const isDarkMode = useSelector(reduxSelectors.app.getIsDarkMode);

  const config = useMemo<NetworkDiagnosticsRoutingData>(
    () => (!isEmpty(value) ? value : getNetworkDiagnosticsRoutingData()),
    [value]
  );

  /*********/
  /* fetch */
  /*********/
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);
  const [fetchedData, setFetchedData] =
    useState<NetworkDiagnosticsRoutingResponse>();

  const fetchData = useCallback(async (params: RoutingQueryParams) => {
    setFetchedInProgress(true);
    try {
      const endpoint = `${apiBaseUrl}/communication-node/network-routing-grid/`;
      const resp = await API.get<NetworkDiagnosticsRoutingResponse>(endpoint, {
        params,
      });
      setFetchedData(resp.data);
    } catch (error: any) {
      const errors = error.response.data?.message
        ? [error.response.data?.message]
        : [];
      setFetchedErrors(errors);
    }
    setFetchedInProgress(false);
  }, []);

  const params = useMemo(
    () => ({
      date_start: config?.params?.date_start ?? dayjs().format('YYYY-MM-DD'),
      date_end: config?.params?.date_end ?? dayjs().format('YYYY-MM-DD'),
      page: config.params.page ?? 0,
      limit: config?.params?.limit ?? 10,
      order: config?.params?.order ?? 'external_id',
      dir: config?.params?.dir ?? 'ASC',
      section_ids: config?.params?.section_ids ?? undefined,
    }),
    [config?.params]
  );

  const formik = useFormik({
    initialValues: params,
    onSubmit: (v) => {
      onUpdate?.(
        update(config, {
          params: {$set: v},
        })
      );
    },
  });

  const handleFetchData = async () => {
    await fetchData(params);
  };

  useEffect(() => {
    if (!_.isEqual(params, formik.values)) {
      formik.setValues(params);
    }
    handleFetchData();
  }, [params]);

  useEffect(() => {
    if (!_.isEqual(params, formik.values)) {
      formik.handleSubmit();
    }
  }, [formik.values]);

  /*************/
  /* data grid */
  /*************/
  const dataGridRef = useRef<DataGridRef>(null);

  const shownFields = useMemo(
    () => config?.shownFields ?? DEFAULT_SHOWN_FIELDS,
    [config?.shownFields]
  );

  const rows = useMemo<NetworkDiagnosticsRoutingItem[]>(
    () => fetchedData?.items ?? [],
    [fetchedData]
  );

  const handleShownFieldsChange = useCallback(
    (fields: string[]) => {
      onUpdate?.(
        update(config, {
          shownFields: {$set: fields},
          exportFields: {
            $set: collectExportFields(fields),
          },
        })
      );
    },
    [onUpdate, config]
  );

  const handleColumnOrderAndShownFieldsChange = useCallback(
    (order: string[], fields: string[]) => {
      onUpdate?.(
        update(config, {
          columnOrder: {$set: order},
          shownFields: {$set: fields},
          exportFields: {
            $set: collectExportFields(fields),
          },
        })
      );
    },
    [onUpdate, config]
  );

  const collectExportFields = (fields?: string[]) => {
    return columns
      .filter((col) => !!col.headerName)
      .map((col) => ({
        field: col.field,
        label: col.headerName,
        hidden: !!fields && !fields.includes(col.field),
      }));
  };

  /*******************/
  /* multiple select */
  /*******************/

  const [newSelectedItemIds, setNewSelectedItemIds] = useState(
    value?.selectedIds ?? []
  );

  const columns: DataGridColumn<NetworkDiagnosticsRoutingItem>[] = useMemo(
    () => [
      {
        headerName: '',
        field: 'select',
        type: 'select',
        width: 60,
        sortable: true,
        renderHeader: () => (
          <Checkbox
            color="primary"
            disabled={fetchedData?.items.length === 0}
            indeterminate={
              newSelectedItemIds.length > 0 &&
              newSelectedItemIds.length < rows.length
            }
            checked={
              rows.length > 0 && newSelectedItemIds.length === rows.length
            }
            onChange={toogleSelectAllItemIds}
          />
        ),
        renderCell: ({row}) => (
          <Checkbox
            color="primary"
            checked={newSelectedItemIds.includes(row.external_id)}
            onChange={() => toogleSelectItemId(row.external_id)}
          />
        ),
      },
      {
        field: 'external_id',
        headerName: 'Network ID',
        width: 120,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => +row.external_id,
      },
      {
        field: 'name',
        headerName: 'Name',
        sortable: true,
        width: 120,
        resizeable: true,
      },
      {
        field: 'routing_parent_1_address',
        headerName: 'Parent 1 Address',
        width: 140,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.routing_parent_1_address,
      },
      {
        field: 'routing_parent_1_rssi',
        headerName: 'Parent 1 Rssi',
        width: 110,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.routing_parent_1_rssi,
      },
      {
        field: 'routing_parent_1_percentage',
        headerName: 'Parent 1 Percentage',
        width: 160,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.routing_parent_1_percentage,
      },
      {
        field: 'routing_parent_2_address',
        headerName: 'Parent 2 Address',
        width: 140,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.routing_parent_2_address,
      },
      {
        field: 'routing_parent_2_rssi',
        headerName: 'Parent 2 Rssi',
        width: 110,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.routing_parent_2_rssi,
      },
      {
        field: 'routing_parent_2_percentage',
        headerName: 'Parent 2 Percentage',
        width: 160,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.routing_parent_2_percentage,
      },
      {
        field: 'routing_parent_3_address',
        headerName: 'Parent 3 Address',
        width: 140,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.routing_parent_3_address,
      },
      {
        field: 'routing_parent_3_rssi',
        headerName: 'Parent 3 Rssi',
        width: 110,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.routing_parent_3_rssi,
      },
      {
        field: 'routing_parent_3_percentage',
        headerName: 'Parent 3 Percentage',
        width: 160,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.routing_parent_3_percentage,
      },
      {
        field: 'routing_parent_4_address',
        headerName: 'Parent 4 Address',
        width: 140,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.routing_parent_4_address,
      },
      {
        field: 'routing_parent_4_rssi',
        headerName: 'Parent 4 Rssi',
        width: 110,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.routing_parent_4_rssi,
      },
      {
        field: 'routing_parent_4_percentage',
        headerName: 'Parent 4 Percentage',
        width: 160,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.routing_parent_4_percentage,
      },
      {
        field: 'routing_parent_5_address',
        headerName: 'Parent 5 Address',
        width: 140,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.routing_parent_5_address,
      },
      {
        field: 'routing_parent_5_rssi',
        headerName: 'Parent 5 Rssi',
        width: 110,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.routing_parent_5_rssi,
      },
      {
        field: 'routing_parent_5_percentage',
        headerName: 'Parent 5 Percentage',
        width: 160,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.routing_parent_5_percentage,
      },
      {
        field: 'routing_min_hops',
        headerName: 'Min Hops',
        width: 80,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.routing_min_hops,
      },
      {
        field: 'routing_max_hops',
        headerName: 'Max Hops',
        width: 80,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.routing_max_hops,
      },
      {
        field: 'routing_mean_hops',
        headerName: 'Mean Hops',
        width: 80,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.routing_mean_hops,
      },
      {
        field: 'routing_lost_parent_count',
        headerName: 'Lost Parent Count',
        width: 120,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.routing_lost_parent_count,
      },
      {
        field: 'routing_changed_parent_count',
        headerName: 'Changed Parent Count',
        width: 120,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.routing_changed_parent_count,
      },
    ],
    [fetchedData, rows, newSelectedItemIds]
  );

  const toogleSelectAllItemIds = () => {
    if (newSelectedItemIds?.length === rows.length) {
      setNewSelectedItemIds([]);
    } else {
      setNewSelectedItemIds(rows.map((_i) => _i.external_id));
    }
  };

  const toogleSelectItemId = (id: number) => {
    if (newSelectedItemIds.includes(id)) {
      setNewSelectedItemIds(newSelectedItemIds.filter((i) => i !== id));
    } else {
      setNewSelectedItemIds([...newSelectedItemIds, id]);
    }
  };

  useEffect(() => {
    const newValue = newSelectedItemIds ?? [];
    if (!_.isEqual(newValue, value?.selectedIds)) {
      onUpdate?.(
        update(value, {
          selectedIds: {
            $set: newValue,
          },
        })
      );
    }
  }, [newSelectedItemIds, value]);

  useEffect(() => {
    const newValue = value?.selectedIds ?? [];
    if (!_.isEqual(newSelectedItemIds, newValue)) {
      setNewSelectedItemIds?.(newValue);
    }
  }, [value?.selectedIds]);

  const handleColumnWidthChange = (widths: Record<string, number>) => {
    onUpdate?.(
      update(config, {
        columnWidths: {$set: widths},
      })
    );
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      overflow="hidden"
      width="100%"
    >
      <Grid container spacing={1} p={2} minWidth={800}>
        <Grid item xs={4}>
          <DateRangeSelect
            value={
              value
                ? [
                    dayjs(value?.params?.date_start).toDate(),
                    dayjs(value?.params?.date_end).toDate(),
                  ]
                : undefined
            }
            size="small"
            disabled={fetchedInProgress}
            onChange={(value) => {
              formik.setFieldValue(
                'date_start',
                dayjs(value[0] ?? undefined).format('YYYY-MM-DD')
              );
              formik.setFieldValue(
                'date_end',
                dayjs(value[1] ?? undefined).format('YYYY-MM-DD')
              );
            }}
          />
        </Grid>

        <Grid item xs={4}>
          <SectionMultipleSelect
            value={formik.values.section_ids}
            size="small"
            onChange={(v) => formik.setFieldValue('section_ids', v)}
          />
        </Grid>

        <Grid item xs={4} display="flex" justifyContent="end">
          <ButtonGroup>
            <Button size="small">
              <NetworkDiagnosticsRoutingExportButton
                params={params}
                exportFields={
                  config.exportFields.length > 0
                    ? config.exportFields
                    : collectExportFields()
                }
              />
            </Button>

            <Button size="small" onClick={() => handleFetchData()}>
              <RefreshIcon />
            </Button>

            <Button
              size="small"
              onClick={() => dataGridRef.current?.printTable()}
            >
              <PrintIcon />
            </Button>
          </ButtonGroup>
        </Grid>
      </Grid>

      <Backdrop
        open={fetchedInProgress}
        sx={{
          position: 'absolute',
          zIndex: 1001,
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {fetchedErrors.map((error, index) => (
        <Alert key={index} severity="error" sx={{mb: 2}}>
          {error}
        </Alert>
      ))}

      <DataGrid
        ref={dataGridRef}
        rows={rows}
        columns={columns}
        loading={fetchedInProgress}
        size="small"
        pagination
        paginationMode="server"
        sortingMode="server"
        rowCount={fetchedData?.count ?? params.page * params.limit}
        page={formik.values.page}
        pageSize={formik.values.limit}
        shownFields={shownFields}
        defaultShownFields={DEFAULT_SHOWN_FIELDS}
        columnWidths={config.columnWidths}
        columnOrder={config.columnOrder}
        onPageChange={(v) => formik.setFieldValue('page', v)}
        onPageSizeChange={(v) => {
          formik.setFieldValue('limit', v);
          formik.setFieldValue('page', 0);
        }}
        onShownFieldsChange={handleShownFieldsChange}
        onColumnWidthsChange={handleColumnWidthChange}
        onColumnOrderAndShownFieldsChange={handleColumnOrderAndShownFieldsChange}
        onSort={(v) => {
          formik.setFieldValue('order', v?.field);
          formik.setFieldValue('dir', v?.dir.toUpperCase());
        }}
        sxFooter={{bgcolor: isDarkMode ? '#2E2E2E' : '#FFF'}}
      />
    </Box>
  );
};
